import { MESES } from "@/shared/lib/datas";

/**
 * Datas e prazos do elo Comunicados.
 *
 * Decisão D6 (06/10/2026): TODAS as igrejas usam o fuso fixo de São Paulo —
 * o mesmo que o banco usa em `comunicado_expira_em()` (30 dias contados em
 * dias de São Paulo). Por isso este arquivo nunca usa o fuso do navegador:
 * "hoje", "ontem" e "apaga amanhã" têm o mesmo significado para todo mundo,
 * esteja a pessoa onde estiver.
 *
 * O banco é a fonte da verdade do prazo (RLS esconde o comunicado vencido no
 * instante exato e uma rotina diária consolida o estado). Estas funções só
 * espelham a conta para exibir rótulos e avisos — nunca decidem visibilidade.
 */
export const FUSO_COMUNICADOS = "America/Sao_Paulo";
export const DIAS_DE_PRAZO = 30;

const MS_POR_DIA = 86_400_000;

const formatadorDia = new Intl.DateTimeFormat("en-CA", {
  timeZone: FUSO_COMUNICADOS,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const formatadorPartes = new Intl.DateTimeFormat("en-US", {
  timeZone: FUSO_COMUNICADOS,
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hourCycle: "h23",
});

/** Dia civil no fuso de São Paulo, no formato "YYYY-MM-DD". */
export function diaNoFuso(data: Date): string {
  return formatadorDia.format(data);
}

/** Diferença em dias civis entre dois "YYYY-MM-DD" (b - a). */
function diasEntreDias(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / MS_POR_DIA);
}

/** Relógio de parede de São Paulo expresso como se fosse UTC (para somar dias com segurança). */
function paredeNoFusoComoUtc(data: Date): number {
  const p: Record<string, number> = {};
  for (const parte of formatadorPartes.formatToParts(data)) {
    if (parte.type !== "literal") p[parte.type] = Number(parte.value);
  }
  return Date.UTC(p.year ?? 1970, (p.month ?? 1) - 1, p.day ?? 1, p.hour ?? 0, p.minute ?? 0, p.second ?? 0);
}

/**
 * Instante em que o comunicado marcado para "apagar sozinho" deixa de existir:
 * 30 dias depois do início da contagem, no relógio de parede de São Paulo.
 * Espelha `comunicado_expira_em()` do banco.
 */
export function apagaEm(contagemInicioEm: string): Date {
  const inicio = new Date(contagemInicioEm);
  const parede = paredeNoFusoComoUtc(inicio);
  const alvoParede = parede + DIAS_DE_PRAZO * MS_POR_DIA;
  // Converte o relógio de parede de volta para um instante (segunda passada
  // cobre mudança de horário de verão entre o início e o alvo).
  const primeiro = new Date(alvoParede - (parede - inicio.getTime()));
  return new Date(alvoParede - (paredeNoFusoComoUtc(primeiro) - primeiro.getTime()));
}

/** Dias civis que faltam para apagar (0 = hoje, 1 = amanhã, negativo = já passou). */
export function diasParaApagar(contagemInicioEm: string, agora: Date = new Date()): number {
  return diasEntreDias(diaNoFuso(agora), diaNoFuso(apagaEm(contagemInicioEm)));
}

/** true se o prazo de 30 dias já terminou (ligar "apagar sozinho" apagaria na hora). */
export function prazoJaTerminou(contagemInicioEm: string, agora: Date = new Date()): boolean {
  return agora.getTime() >= apagaEm(contagemInicioEm).getTime();
}

/** "6 out", no fuso de São Paulo. */
export function dataCurtaNoFuso(data: Date): string {
  const dia = diaNoFuso(data);
  return `${Number(dia.slice(8, 10))} ${MESES[Number(dia.slice(5, 7)) - 1]}`;
}

/** "Hoje", "Ontem" ou "6 out" para a data de publicação. */
export function rotuloPublicado(publicadoEm: string, agora: Date = new Date()): string {
  const data = new Date(publicadoEm);
  const diferenca = diasEntreDias(diaNoFuso(data), diaNoFuso(agora));
  if (diferenca === 0) return "Hoje";
  if (diferenca === 1) return "Ontem";
  return dataCurtaNoFuso(data);
}

/** "Apaga hoje", "Apaga amanhã" ou "Apaga em 12 dias". */
export function rotuloApagaEm(contagemInicioEm: string, agora: Date = new Date()): string {
  const dias = diasParaApagar(contagemInicioEm, agora);
  if (dias <= 0) return "Apaga hoje";
  if (dias === 1) return "Apaga amanhã";
  return `Apaga em ${dias} dias`;
}
